import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  CreditCard,
  Settings,
  BarChart3,
} from "lucide-react";
import { useMobile } from "./MobileOptimized";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", ariaLabel: "Go to Dashboard" },
  { to: "/invoices", icon: FileText, label: "Invoices", ariaLabel: "Go to Invoices" },
  { to: "/customers", icon: Users, label: "Customers", ariaLabel: "Go to Customers" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", ariaLabel: "Go to Analytics" },
  { to: "/settings", icon: Settings, label: "Settings", ariaLabel: "Go to Settings" },
];

const MobileNav = () => {
  const { isMobile, isTouchDevice } = useMobile();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Auto-hide navigation on scroll down, show on scroll up
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;
      const scrollThreshold = 10;

      if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) return;

      setIsVisible(!scrollingDown || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, lastScrollY]);

  // This component should only render on mobile devices.
  if (!isMobile) {
    return null;
  }

  return (
    <nav
      id="mobile-nav"
      aria-label="Mobile navigation"
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out mobile-safe-area",
        "bg-background/95 backdrop-blur-sm border-t border-border shadow-lg",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}
      role="navigation"
    >
      <div className="flex justify-around items-center h-16 max-w-md mx-auto px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.to || 
            (item.to !== "/" && location.pathname.startsWith(item.to));
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              aria-label={item.ariaLabel}
              className={cn(
                "flex flex-col items-center justify-center w-full h-full text-sm font-medium",
                "transition-all duration-200 ease-in-out transform-gpu touch-target touch-feedback",
                "relative rounded-lg min-w-[48px]",
                isActive
                  ? "text-primary scale-110"
                  : "text-muted-foreground hover:text-primary/90 hover:scale-105 active:scale-95",
                isTouchDevice && "hover:bg-muted/30"
              )}
            >
              {/* Active indicator */}
              {isActive && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-primary rounded-full" />
              )}
              
              {/* Icon with notification badge support */}
              <div className="relative">
                <Icon className="h-5 w-5" aria-hidden="true" />
                {/* Future: notification badge */}
                {/* {item.badge && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
                )} */}
              </div>
              
              <span className="mt-1 text-[10px] tracking-tight leading-tight">
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
      
      {/* Safe area spacer for devices with home indicators */}
      <div className="h-2 bg-background/95" />
    </nav>
  );
};

export default MobileNav;
